import React, { useEffect, useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import PageHeader from "../shared-components/PageHeader";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import ReactTooltip from "react-tooltip";
import {
  DateRangePicker,
  DateRangeValue,
} from "../shared-components/DateRangePicker";
import { Modal } from "../shared-components/Modal";
import FilterComponent from "../shared-components/Filter";
import CompletedTableFilter from "../admin/admin-completed/CompletedTableFilter";
import PaginationInMemory from "../shared-components/PaginationInMemory";
import { ListViewControl, useControlGroupContext } from "../api/ControlGroup";
import {
  getFilterValues,
  hasFilterValues,
} from "../admin/admin-completed/AdminUtil";
import {
  useCompaniesFetchAll,
  useReportsLookup,
  useReportsLookupExport,
} from "../api/CompaniesApi";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  CompaniesApi,
  formatReportsLookupStatus,
  ReportsLookupRecord,
} from "../api/Companies";
import { KeyValue } from "../reports/utils";

const COMPANY_FILTER_KEY = "company_uuid";

/** Fresh snapshot each call — avoids shared refs so reset always updates React state. */
function createInitialCompanyControlGroups(): ListViewControl[] {
  return [
    { name: COMPANY_FILTER_KEY, controls: { direction: "", filters: [] } },
  ];
}

function getCompanyId(item: CompaniesApi): string {
  return item.external_id ?? item.name;
}

const DATE_RANGE_TOOLTIP_ID = "usage-report-date-range-tooltip";
const DATE_RANGE_TOOLTIP_TEXT =
  "Select a date range to see reports completed within that period.";

const TABLE_HEADERS = [
  "Company name",
  "Finished date",
  "Request date",
  "Candidate's name",
  "Check type",
  "Behavior kit",
  "Report status",
  "Report ID",
  "Requested by",
];

const PAGE_SIZE = 50;

function formatDateOnly(iso: string): string {
  try {
    return format(parseISO(iso), "MM/dd/yyyy");
  } catch {
    return iso;
  }
}

const UsageReport = () => {
  const { controlGroups, setControlGroups } = useControlGroupContext();
  const {
    mutate: getAllCompanies,
    data: companiesData,
    isLoading,
    isIdle,
  } = useCompaniesFetchAll();
  const reportsLookup = useReportsLookup();
  const reportsLookupExport = useReportsLookupExport();
  const { canDownloadUsageReport } = usePermissionValidator();
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [filterKey, setFilterKey] = useState(0);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastRequest, setLastRequest] = useState<{
    company_uuids: string[];
    start_date: string;
    finish_date: string;
  } | null>(null);

  const companyControlGroup = controlGroups.find(
    (g) => g.name === COMPANY_FILTER_KEY
  ) ?? { name: COMPANY_FILTER_KEY, controls: { direction: "", filters: [] } };
  const companies = useMemo(() => companiesData ?? [], [companiesData]);

  useEffect(() => {
    getAllCompanies();
  }, [getAllCompanies]);

  const filterData = useMemo(() => {
    const list: KeyValue[] = companies.map((c) => ({
      key: getCompanyId(c),
      value: c.name,
    }));
    return { [COMPANY_FILTER_KEY]: list };
  }, [companies]);

  const companyFilterValues = getFilterValues(
    controlGroups,
    COMPANY_FILTER_KEY as "company_uuid",
    filterData
  );
  const hasCompanyFilter = hasFilterValues(
    controlGroups,
    COMPANY_FILTER_KEY as "company_uuid"
  );
  const selectedCompanyIds = companyControlGroup.controls.filters ?? [];

  /** Snapshot when company modal opens; restored on cancel/close without save. */
  const companyFilterModalSnapshotRef = useRef<ListViewControl | null>(null);

  const hasInitializedCompanyFilter = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    // useMutation starts idle with isLoading false; wait until the first fetch settles
    if (isIdle) return;
    if (hasInitializedCompanyFilter.current) return;
    hasInitializedCompanyFilter.current = true;
    if (companies.length === 1) {
      setControlGroups([
        {
          name: COMPANY_FILTER_KEY,
          controls: {
            direction: "",
            filters: [getCompanyId(companies[0])],
          },
        },
      ]);
    } else {
      setControlGroups(createInitialCompanyControlGroups());
    }
  }, [companies, isLoading, isIdle, setControlGroups]);

  const handleSubmit = () => {
    if (!dateRange || selectedCompanyIds.length === 0) return;
    companyFilterModalSnapshotRef.current = null;
    setOpenModal(null);
    setHasSubmitted(true);
    setCurrentPage(1);
    const req = {
      company_uuids: selectedCompanyIds,
      start_date: dateRange.startDate,
      finish_date: dateRange.endDate,
      page: 1,
      page_size: PAGE_SIZE,
      skip_count: false,
    };
    setLastRequest({
      company_uuids: selectedCompanyIds,
      start_date: dateRange.startDate,
      finish_date: dateRange.endDate,
    });
    reportsLookup.mutate(req);
  };

  const fetchPage = (page: number) => {
    if (!lastRequest) return;
    setCurrentPage(page);
    reportsLookup.mutate({
      ...lastRequest,
      page,
      page_size: PAGE_SIZE,
      skip_count: false,
    });
  };

  const handleClearFilters = () => {
    setDateRange(null);
    setHasSubmitted(false);
    setLastRequest(null);
    setFilterKey((k) => k + 1);
    reportsLookup.reset();
    if (companies.length === 1) {
      setControlGroups([
        {
          name: COMPANY_FILTER_KEY,
          controls: {
            direction: "",
            filters: [getCompanyId(companies[0])],
          },
        },
      ]);
    } else {
      setControlGroups(createInitialCompanyControlGroups());
    }
  };

  const handleOpenModal = (modalKey: string) => {
    if (modalKey === COMPANY_FILTER_KEY) {
      const g = controlGroups.find((x) => x.name === COMPANY_FILTER_KEY);
      companyFilterModalSnapshotRef.current = g
        ? {
            name: g.name,
            controls: {
              direction: g.controls.direction,
              filters: [...(g.controls.filters ?? [])],
            },
          }
        : {
            name: COMPANY_FILTER_KEY,
            controls: { direction: "", filters: [] },
          };
    }
    setOpenModal(modalKey);
  };

  const handleCloseModal = () => {
    const snap = companyFilterModalSnapshotRef.current;
    if (openModal === COMPANY_FILTER_KEY && snap) {
      setControlGroups((prev) => {
        const idx = prev.findIndex((x) => x.name === COMPANY_FILTER_KEY);
        if (idx === -1) {
          return [
            {
              name: snap.name,
              controls: {
                direction: snap.controls.direction,
                filters: [...(snap.controls.filters ?? [])],
              },
            },
          ];
        }
        const next = [...prev];
        next[idx] = {
          name: snap.name,
          controls: {
            direction: snap.controls.direction,
            filters: [...(snap.controls.filters ?? [])],
          },
        };
        return next;
      });
    }
    companyFilterModalSnapshotRef.current = null;
    setOpenModal(null);
  };

  const handleSaveFilter = () => {
    companyFilterModalSnapshotRef.current = null;
    setOpenModal(null);
  };

  const handleExport = () => {
    if (lastRequest) {
      reportsLookupExport.mutate({
        company_uuids: lastRequest.company_uuids,
        start_date: lastRequest.start_date,
        finish_date: lastRequest.finish_date,
        skip_count: true,
      });
      return;
    }
    if (!dateRange || selectedCompanyIds.length === 0) return;
    reportsLookupExport.mutate({
      company_uuids: selectedCompanyIds,
      start_date: dateRange.startDate,
      finish_date: dateRange.endDate,
      skip_count: true,
    });
  };

  const today = useMemo(() => new Date(), []);

  return (
    <>
      <div className="w-full px-4 pb-3">
        <PageHeader title="Usage Report">
          {canDownloadUsageReport && (
            <Button
              type={ButtonTypes.download}
              colorType={ButtonColorTypes.primary}
              text={reportsLookupExport.isLoading ? "Exporting…" : "Export"}
              onClick={handleExport}
              disabled={
                !dateRange ||
                selectedCompanyIds.length === 0 ||
                isLoading ||
                reportsLookupExport.isLoading
              }
              className="px-5 py-2.5 text-base rounded-lg border-0"
              data-testid="usage-report-export-button"
            />
          )}
        </PageHeader>
      </div>

      <div className="w-full flex">
        <section
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm w-fit"
          aria-label="Filtering section"
        >
          <div className="flex items-center gap-2 flex-wrap w-full">
            <div
              className={
                isLoading ? "relative opacity-60 pointer-events-none" : ""
              }
            >
              {isLoading && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex">
                  <LoadingSpinner className="!w-5 !h-5" />
                </span>
              )}
              <CompletedTableFilter
                hasFilter={hasCompanyFilter}
                filterValues={companyFilterValues}
                setOpenModal={handleOpenModal}
                title="Please select a Company"
                modalKey={COMPANY_FILTER_KEY}
                groupBy={COMPANY_FILTER_KEY}
                groupByLabel="Company/Companies"
                readOnly={hasSubmitted}
              />
            </div>
            <DateRangePicker
              key={`date-${filterKey}`}
              onRangeChange={setDateRange}
              tooltipId={DATE_RANGE_TOOLTIP_ID}
              tooltipText={DATE_RANGE_TOOLTIP_TEXT}
              maxStartDate={today}
              maxEndDate={today}
              readOnly={hasSubmitted}
            />
            {hasSubmitted ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 text-sm font-medium rounded-md bg-ourGreen hover:bg-ourGreenHover text-white"
                data-testid="usage-report-clear-button"
              >
                CLEAR
              </button>
            ) : (
              <button
                type="button"
                disabled={
                  !dateRange || selectedCompanyIds.length === 0 || isLoading
                }
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium rounded-md disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400 bg-ourGreen hover:bg-ourGreenHover text-white"
                data-testid="usage-report-submit-button"
              >
                Submit
              </button>
            )}
          </div>
          <ReactTooltip
            id={DATE_RANGE_TOOLTIP_ID}
            effect="solid"
            place="right"
            delayHide={100}
          >
            {DATE_RANGE_TOOLTIP_TEXT}
          </ReactTooltip>
        </section>

        {!hasSubmitted &&
          openModal === COMPANY_FILTER_KEY &&
          filterData[COMPANY_FILTER_KEY] && (
            <Modal
              open={openModal === COMPANY_FILTER_KEY}
              width="w-[600px]"
              onClose={handleCloseModal}
            >
              <FilterComponent
                controlGroup={companyControlGroup}
                date={false}
                parentList={filterData[COMPANY_FILTER_KEY]}
                loading={isLoading}
                saveFilter={handleSaveFilter}
                cleared={handleSaveFilter}
                showSortHeader={false}
                canceled={handleCloseModal}
              />
            </Modal>
          )}
      </div>

      <section
        className="w-full px-4 mt-3 rounded-lg border border-gray-200 bg-white p-4 min-h-[200px] overflow-x-auto shadow-sm"
        aria-label="Data grid"
      >
        <table className="w-full min-w-[800px]">
          <caption className="sr-only">
            Sorted by company name (A–Z), then oldest finish date, then oldest
            request date.
          </caption>
          <thead>
            <tr className="border-b border-gray-200 text-left text-sm font-medium text-gray-700">
              {TABLE_HEADERS.map((header) => (
                <th key={header} className="py-3 px-4 whitespace-nowrap">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportsLookup.isLoading && (
              <tr>
                <td colSpan={TABLE_HEADERS.length} className="py-8 text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            )}
            {!reportsLookup.isLoading && reportsLookup.error && (
              <tr>
                <td
                  colSpan={TABLE_HEADERS.length}
                  className="py-8 text-center text-red-600"
                  data-testid="usage-report-grid-error"
                >
                  <p className="text-sm">
                    Failed to load data. Please try again.
                  </p>
                </td>
              </tr>
            )}
            {!reportsLookup.isLoading &&
              !reportsLookup.error &&
              (reportsLookup.data?.records?.length ?? 0) === 0 && (
                <tr>
                  <td
                    colSpan={TABLE_HEADERS.length}
                    className="py-8 text-center text-gray-500"
                    data-testid={
                      hasSubmitted
                        ? "usage-report-grid-no-data-for-range"
                        : "usage-report-grid-empty-state"
                    }
                  >
                    <p className="text-sm">
                      {hasSubmitted
                        ? "No reports found for this date range. Try a different range or company selection."
                        : "No data to display. Use the filters above and click Submit to see reports completed within that period."}
                    </p>
                  </td>
                </tr>
              )}
            {!reportsLookup.isLoading &&
              !reportsLookup.error &&
              (reportsLookup.data?.records?.length ?? 0) > 0 &&
              (reportsLookup.data?.records ?? []).map(
                (row: ReportsLookupRecord) => (
                  <tr
                    key={row.report_uuid}
                    className="border-b border-gray-100 text-sm text-gray-700"
                  >
                    <td className="py-2 px-4 max-w-[160px] min-w-0">
                      <span className="truncate block" title={row.company_name}>
                        {row.company_name}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[100px] min-w-0">
                      <span
                        className="truncate block"
                        title={formatDateOnly(row.delivered_at)}
                      >
                        {formatDateOnly(row.delivered_at)}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[100px] min-w-0">
                      <span
                        className="truncate block"
                        title={formatDateOnly(row.created_at)}
                      >
                        {formatDateOnly(row.created_at)}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[140px] min-w-0">
                      <span className="truncate block" title={row.person_name}>
                        {row.person_name}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[140px] min-w-0">
                      <span
                        className="truncate block"
                        title={row.blueprint_name}
                      >
                        {row.blueprint_name}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[140px] min-w-0">
                      <span
                        className="truncate block"
                        title={row.behavior_kit_name}
                      >
                        {row.behavior_kit_name}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[100px] min-w-0">
                      <span
                        className="truncate block"
                        title={formatReportsLookupStatus(row.status)}
                      >
                        {formatReportsLookupStatus(row.status)}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[140px] min-w-0">
                      <span className="truncate block" title={row.report_uuid}>
                        {row.report_uuid}
                      </span>
                    </td>
                    <td className="py-2 px-4 max-w-[140px] min-w-0">
                      <span className="truncate block" title={row.requested_by}>
                        {row.requested_by}
                      </span>
                    </td>
                  </tr>
                )
              )}
          </tbody>
        </table>
        {reportsLookup.data &&
          reportsLookup.data.pages > 1 &&
          !reportsLookup.isLoading && (
            <PaginationInMemory
              totalCount={reportsLookup.data.total}
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              pageChange={fetchPage}
              showDescription
            />
          )}
      </section>
    </>
  );
};

export default UsageReport;
